import {
  countPendingAndFailed,
  listClientsCached,
  listJobsBySync,
  listSitesCached,
  listWorkTypesCached,
  putClientCached,
  setMeta,
  getMeta,
} from '../db/jobsDb.js';
import {
  logout, sessionLooksValid, fetchXeroStatus, disconnectXeroRemote, syncXeroInvoices,
  pushClientXeroRemote,
  linkClientXeroRemote,
} from '../api/client.js';
import {
  clientNeedsXeroAttention,
  xeroStatusLabel,
} from '../clients/xeroStatus.js';
import { exportPendingText } from './status.js';
import { mountRenewals } from './renewals.js';
import { mountExpenses } from './expenses.js';
import {
  listEmptyHtml,
  listErrorHtml,
  listSkeletonHtml,
} from './listStates.js';
import {
  getThemePreference,
  setThemePreference,
  type ThemePreference,
} from '../theme/theme.js';

export type MoreHooks = {
  onSignedIn: () => void;
  onSignedOut: () => void;
  onSync: () => void;
  onChange: () => void;
};

type MoreView =
  | 'menu'
  | 'settings'
  | 'clients'
  | 'work-types'
  | 'sites'
  | 'expenses'
  | 'compliance';

export async function mountMore(
  root: HTMLElement,
  hooks: MoreHooks,
): Promise<{
  refresh: () => Promise<void>;
  showSettings: () => void;
  showRenewals: (focusId?: string) => void;
  showExpenses: () => void;
  showCompliance: (focusId?: string) => void;
}> {
  let view: MoreView = 'menu';
  let complianceFocus: string | null = null;

  async function refresh() {
    if (view === 'menu') await renderMenu();
    else if (view === 'settings') await renderSettings();
    else if (view === 'clients') await renderClients();
    else if (view === 'work-types') await renderWorkTypes();
    else if (view === 'sites') await renderSites();
    else if (view === 'expenses') {
      await mountExpenses(root, {
        onChange: () => hooks.onChange(),
        onBack: () => {
          view = 'menu';
          void refresh();
        },
        onOpenCompliance: () => showCompliance(),
      });
    } else if (view === 'compliance') {
      await mountRenewals(root, {
        onChange: () => hooks.onChange(),
        focusId: complianceFocus,
      });
      complianceFocus = null;
      root.querySelector('[data-back]')?.addEventListener('click', () => {
        view = 'menu';
        void refresh();
      });
    }
  }

  function showSettings() {
    view = 'settings';
    void refresh();
  }

  function showExpenses() {
    view = 'expenses';
    void refresh();
  }

  function showCompliance(focusId?: string) {
    complianceFocus = focusId ?? null;
    view = 'compliance';
    void refresh();
  }

  /** @deprecated Prefer showCompliance — kept for header renewal deep-links. */
  function showRenewals(focusId?: string) {
    showCompliance(focusId);
  }

  async function renderMenu() {
    root.innerHTML = `
      <section class="more-menu">
        <h1 class="screen-title">More</h1>
        <nav class="more-list">
          <button type="button" data-go="expenses">Expenses</button>
          <button type="button" data-go="compliance">Compliance</button>
          <button type="button" data-go="clients">Clients</button>
          <button type="button" data-go="work-types">Work types</button>
          <button type="button" data-go="sites">Sites</button>
          <button type="button" data-go="settings">Settings</button>
        </nav>
      </section>
    `;
    root.querySelector('[data-go="expenses"]')?.addEventListener('click', () => {
      showExpenses();
    });
    root.querySelector('[data-go="compliance"]')?.addEventListener('click', () => {
      showCompliance();
    });
    root.querySelector('[data-go="clients"]')?.addEventListener('click', () => {
      view = 'clients';
      void refresh();
    });
    root.querySelector('[data-go="work-types"]')?.addEventListener('click', () => {
      view = 'work-types';
      void refresh();
    });
    root.querySelector('[data-go="sites"]')?.addEventListener('click', () => {
      view = 'sites';
      void refresh();
    });
    root.querySelector('[data-go="settings"]')?.addEventListener('click', () => {
      view = 'settings';
      void refresh();
    });
  }

  async function renderSettings() {
    const signedIn = await sessionLooksValid();
    const { pending, failed } = await countPendingAndFailed();
    const dismissed = await getMeta('onboarding_homescreen_dismissed');
    const themePref = await getThemePreference();
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      ('standalone' in navigator &&
        (navigator as Navigator & { standalone?: boolean }).standalone === true);

    let xeroLabel = 'Sign in online first';
    let xeroDetail = 'Unlock with your password while online so a server session exists, then connect Xero.';
    let xeroCanConnect = false;
    let xeroConnected = false;
    if (signedIn) {
      try {
        const st = await fetchXeroStatus();
        xeroConnected = st.status === 'connected';
        if (st.status === 'connected') {
          xeroLabel = st.xero_tenant_name ?? 'Connected';
          xeroDetail = 'Organisation linked. Token refresh runs automatically.';
        } else if (st.status === 'needs_reauth') {
          xeroLabel = 'Needs reconnect';
          xeroDetail = 'Refresh failed — connect again.';
          xeroCanConnect = true;
        } else if (st.status === 'not_configured') {
          xeroLabel = 'Not configured';
          xeroDetail = `Server missing ${(st.missing_env ?? []).join(', ') || 'Xero env'}.`;
        } else {
          xeroLabel = 'Not connected';
          xeroDetail = 'Link your Xero organisation to enable invoice drafts.';
          xeroCanConnect = true;
        }
      } catch {
        xeroLabel = 'Unavailable';
        xeroDetail = 'Could not load Xero status (offline?).';
      }
    }

    const themeOptions: { id: ThemePreference; label: string }[] = [
      { id: 'auto', label: 'Auto' },
      { id: 'light', label: 'Light' },
      { id: 'dark', label: 'Dark' },
    ];

    root.innerHTML = `
      <section class="more-panel">
        <button type="button" class="btn-back" data-back>‹ More</button>
        <h1 class="screen-title">Settings</h1>

        <div class="settings-block">
          <h2>Appearance</h2>
          <p class="muted">Auto follows the phone setting. Light is better in sun; dark indoors.</p>
          <div class="theme-toggle" role="group" aria-label="Theme">
            ${themeOptions
              .map(
                (o) =>
                  `<button type="button" data-theme-pref="${o.id}" aria-pressed="${themePref === o.id}">${o.label}</button>`,
              )
              .join('')}
          </div>
        </div>

        <div class="settings-block">
          <h2>Account</h2>
          ${
            signedIn
              ? `<p class="muted">Unlocked on this device for 14 days after each password entry. Sync uses the same unlock.</p>
                 <button type="button" class="btn-secondary" id="btn-logout">Lock now</button>`
              : `<p class="muted">Locked — enter your password on the unlock screen (works offline). For Xero, unlock while online so a server session is created.</p>`
          }
        </div>

        <div class="settings-block">
          <h2>Xero</h2>
          <p><strong>${escape(xeroLabel)}</strong></p>
          <p class="muted">${escape(xeroDetail)}</p>
          ${
            xeroCanConnect
              ? `<button type="button" class="btn-primary" id="btn-xero-connect">Connect Xero</button>`
              : ''
          }
          ${
            xeroConnected
              ? `<button type="button" class="btn-secondary" id="btn-xero-sync">Sync invoices from Xero</button>
                 <button type="button" class="btn-secondary" id="btn-xero-disconnect">Disconnect Xero</button>`
              : ''
          }
        </div>

        <div class="settings-block">
          <h2>Sync</h2>
          <p class="muted">${pending} pending${failed ? ` · ${failed} failed` : ''}</p>
          <button type="button" class="btn-primary" id="btn-sync">Sync now</button>
          <button type="button" class="btn-secondary" id="btn-export">Export pending</button>
        </div>

        ${
          !standalone && dismissed !== '1'
            ? `<div class="settings-block onboarding">
                <h2>Install</h2>
                <p class="muted">Add to Home Screen so offline jobs survive Safari clearing site data.</p>
                <button type="button" class="btn-secondary" id="dismiss-onboarding">Got it</button>
              </div>`
            : ''
        }
      </section>
    `;

    root.querySelector('[data-back]')?.addEventListener('click', () => {
      view = 'menu';
      void refresh();
    });
    root.querySelectorAll('[data-theme-pref]').forEach((el) => {
      el.addEventListener('click', () => {
        const pref = (el as HTMLElement).dataset.themePref as ThemePreference;
        void setThemePreference(pref).then(() => refresh());
      });
    });
    root.querySelector('#btn-sync')?.addEventListener('click', () => hooks.onSync());
    root.querySelector('#btn-xero-connect')?.addEventListener('click', () => {
      // Full navigation so the session cookie is sent to /xero/connect.
      window.location.assign('/xero/connect');
    });
    root.querySelector('#btn-xero-disconnect')?.addEventListener('click', async () => {
      if (!window.confirm('Disconnect Xero? You will need to reconnect to send drafts.')) {
        return;
      }
      try {
        await disconnectXeroRemote();
        await refresh();
      } catch (err) {
        window.alert(err instanceof Error ? err.message : 'Disconnect failed');
      }
    });
    root.querySelector('#btn-xero-sync')?.addEventListener('click', async () => {
      try {
        const r = await syncXeroInvoices();
        window.alert(r.message);
        hooks.onChange();
        await refresh();
      } catch (err) {
        window.alert(err instanceof Error ? err.message : 'Xero sync failed');
      }
    });
    root.querySelector('#btn-export')?.addEventListener('click', async () => {
      const pendingJobs = await listJobsBySync('pending');
      const syncing = await listJobsBySync('syncing');
      const failedJobs = await listJobsBySync('failed');
      const text = exportPendingText([...pendingJobs, ...syncing, ...failedJobs]);
      try {
        await navigator.clipboard.writeText(text);
        const btn = root.querySelector('#btn-export') as HTMLButtonElement;
        btn.textContent = 'Copied';
        setTimeout(() => {
          btn.textContent = 'Export pending';
        }, 1500);
      } catch {
        window.prompt('Copy pending jobs:', text);
      }
    });
    root.querySelector('#btn-logout')?.addEventListener('click', async () => {
      try {
        await logout();
      } catch (err) {
        console.warn('logout request failed; clearing local session', err);
      }
      hooks.onSignedOut();
    });
    root.querySelector('#dismiss-onboarding')?.addEventListener('click', async () => {
      await setMeta('onboarding_homescreen_dismissed', '1');
      await refresh();
    });
  }

  async function renderClients() {
    root.innerHTML = `
      <section class="more-panel">
        <button type="button" class="btn-back" data-back>‹ More</button>
        <h1 class="screen-title">Clients</h1>
        ${listSkeletonHtml(5, 'list-skel-row skel-ref')}
      </section>
    `;
    root.querySelector('[data-back]')?.addEventListener('click', () => {
      view = 'menu';
      void refresh();
    });

    let listError = false;
    let clients: Awaited<ReturnType<typeof listClientsCached>> = [];
    try {
      clients = await listClientsCached();
    } catch {
      listError = true;
    }

    const body = listError
      ? listErrorHtml()
      : clients.length === 0
        ? listEmptyHtml(
            'No clients cached yet. Unlock online so sync can download your client list.',
          )
        : `<ul class="ref-list client-list">${clients
            .map(
              (c) =>
                `<li><button type="button" class="client-row" data-client="${c.id}">
                  <strong>${escape(c.name)}</strong>
                  <span class="muted">${c.day_rate != null ? `Day £${Number(c.day_rate).toFixed(0)}` : 'No day rate'}${
                    clientNeedsXeroAttention(c)
                      ? ` · ${escape(xeroStatusLabel(c.xero_contact_status))}`
                      : ''
                  }</span>
                </button></li>`,
            )
            .join('')}</ul>`;

    root.innerHTML = `
      <section class="more-panel">
        <button type="button" class="btn-back" data-back>‹ More</button>
        <h1 class="screen-title">Clients</h1>
        <p class="muted">From cache — tap for rates, history, and sites.</p>
        ${body}
      </section>
    `;
    root.querySelector('[data-back]')?.addEventListener('click', () => {
      view = 'menu';
      void refresh();
    });
    root.querySelector('[data-list-retry]')?.addEventListener('click', () => {
      void renderClients();
    });
    root.querySelectorAll('[data-client]').forEach((el) => {
      el.addEventListener('click', () => {
        void renderClientDetail((el as HTMLElement).dataset.client!);
      });
    });
  }

  async function renderClientDetail(clientId: string) {
    const clients = await listClientsCached();
    const found = clients.find((c) => c.id === clientId);
    if (!found) {
      void renderClients();
      return;
    }
    const client = found;
    root.innerHTML = `
      <section class="more-panel">
        <button type="button" class="btn-back" data-back>‹ Clients</button>
        <h1 class="screen-title">${escape(client.name)}</h1>
        ${listSkeletonHtml(3, 'list-skel-row skel-ref')}
      </section>
    `;
    root.querySelector('[data-back]')?.addEventListener('click', () => {
      void renderClients();
    });

    const [{ listSitesCached, listRateOverridesCached, listWorkTypesCached }, api] =
      await Promise.all([
        import('../db/jobsDb.js'),
        import('../api/client.js'),
      ]);
    const [sites, overrides, types, invoices, summary, analytics] =
      await Promise.all([
        listSitesCached(clientId),
        listRateOverridesCached(),
        listWorkTypesCached(),
        api.fetchInvoices({ client_id: clientId, limit: '20' }).catch(() => []),
        api.fetchInvoiceSummary().catch(() => null),
        api.fetchInvoiceAnalytics({ period: 'all' }).catch(() => null),
      ]);

    const clientSum = summary?.per_client.find(
      (c) => c.client_id === clientId || c.client_name === client.name,
    );
    const mix = analytics?.work_type_by_client.find(
      (c) => c.client_id === clientId,
    );
    const payReady = analytics?.avg_days_to_payment.ready.find(
      (c) => c.client_id === clientId || c.client_name === client.name,
    );
    const payThin = analytics?.avg_days_to_payment.insufficient.find(
      (c) => c.client_id === clientId || c.client_name === client.name,
    );
    const clientOverrides = overrides.filter((o) => o.client_id === clientId);

    const {
      prepareDonutSlices,
      renderDonutHtml,
      bindDonutFilter,
      matchesDonutFilter,
    } = await import('./donut.js');

    let mixFilter: string | null = null;
    const mixRows =
      mix?.mix.map((m) => ({
        key: m.code || m.label,
        label: m.label,
        value: m.pence,
        pct: m.pct,
      })) ?? [];
    const mixSlices = prepareDonutSlices(
      mixRows.map((m) => ({ key: m.key, label: m.label, value: m.value })),
    );
    const mixTotal = mixRows.reduce((s, m) => s + m.value, 0);

    function mixSectionHtml(): string {
      if (!mix || mixRows.length === 0) return '';
      const filtered = mixRows.filter((m) =>
        matchesDonutFilter(m.key, mixFilter, mixSlices),
      );
      return `<h2 class="money-subhead">Work type mix</h2>
        <p class="hint">Tap a slice to filter. Shares under 3% group as Others.</p>
        ${renderDonutHtml({
          id: `client-mix-${clientId.slice(0, 8)}`,
          slices: mixSlices,
          totalLabel: `£${(mixTotal / 100).toFixed(2)}`,
          totalSub: 'mix',
          formatValue: (v) => `£${(v / 100).toFixed(2)}`,
          selectedKey: mixFilter,
          ariaLabel: 'Client work type mix',
        })}
        <ul class="money-payer-list">
          ${
            filtered.length === 0
              ? '<li class="muted">No types in this slice. Tap again to clear.</li>'
              : filtered
                  .map(
                    (m) =>
                      `<li><span>${escape(m.label)}</span><strong>${m.pct}%</strong><span class="muted">£${(m.value / 100).toFixed(0)}</span></li>`,
                  )
                  .join('')
          }
        </ul>`;
    }

    const rateCard = `
      <div class="client-rates">
        <h2 class="money-subhead">Rate card</h2>
        <ul class="money-payer-list">
          <li><span>Day</span><strong>${client.day_rate != null ? `£${Number(client.day_rate).toFixed(2)}` : '—'}</strong></li>
          <li><span>Half day</span><strong>${client.half_day_rate != null ? `£${Number(client.half_day_rate).toFixed(2)}` : '—'}</strong></li>
          <li><span>Hourly</span><strong>${client.hourly_rate != null ? `£${Number(client.hourly_rate).toFixed(2)}` : '—'}</strong></li>
        </ul>
        ${
          clientOverrides.length
            ? `<p class="hint">Overrides: ${clientOverrides
                .map((o) => {
                  const t = types.find((x) => x.id === o.work_type_id);
                  return t?.short_description ?? 'Work type';
                })
                .join(', ')}</p>`
            : ''
        }
      </div>`;

    function paintDetail() {
      const donutId = `client-mix-${clientId.slice(0, 8)}`;
      root.innerHTML = `
      <section class="more-panel client-detail">
        <button type="button" class="btn-back" data-back>‹ Clients</button>
        <h1 class="screen-title">${escape(client.name)}</h1>
        <p class="hint">${escape(xeroStatusLabel(client.xero_contact_status))}${
          client.xero_contact_error ? ` — ${escape(client.xero_contact_error)}` : ''
        }</p>
        ${
          clientNeedsXeroAttention(client)
            ? `<div class="client-xero-actions">
                <button type="button" class="btn-secondary" data-xero-retry>Retry Xero</button>
                ${(client.xero_contact_matches ?? [])
                  .map(
                    (m) =>
                      `<button type="button" class="btn-secondary" data-xero-link="${escape(m.ContactID)}">Link “${escape(m.Name)}”</button>`,
                  )
                  .join('')}
              </div>`
            : ''
        }
        ${rateCard}
        <div class="money-summary-row" style="margin:12px 0">
          <div>
            <span class="muted">Total invoiced</span>
            <strong>${
              clientSum
                ? `£${(clientSum.invoiced_pence / 100).toFixed(2)}`
                : '—'
            }</strong>
          </div>
          <div>
            <span class="muted">Avg days to pay</span>
            <strong>${
              payReady
                ? `${payReady.avg_days_to_payment}d (${payReady.paid_count})`
                : payThin
                  ? `Not enough history (${payThin.paid_count})`
                  : '—'
            }</strong>
          </div>
        </div>
        ${mixSectionHtml()}
        <h2 class="money-subhead">Sites worked</h2>
        ${
          sites.length === 0
            ? '<p class="muted">No sites linked to this client.</p>'
            : `<ul class="ref-list">${sites.map((s) => `<li>${escape(s.name)}</li>`).join('')}</ul>`
        }
        <h2 class="money-subhead">Recent invoices</h2>
        ${
          invoices.length === 0
            ? '<p class="muted">No invoices loaded (offline or none yet).</p>'
            : `<ul class="money-list">${invoices
                .map(
                  (inv) => `<li>
                    <button type="button" class="money-row" disabled>
                      <span class="money-row-top">
                        <strong>#${escape(inv.invoice_number ?? '—')}</strong>
                        <span>£${((inv.gross_pence ?? 0) / 100).toFixed(2)}</span>
                      </span>
                      <span class="money-row-bot muted">${escape(inv.invoice_date)} · ${escape(inv.status)}</span>
                    </button>
                  </li>`,
                )
                .join('')}</ul>`
        }
      </section>
    `;
      root.querySelector('[data-back]')?.addEventListener('click', () => {
        void renderClients();
      });
      root.querySelector('[data-xero-retry]')?.addEventListener('click', () => {
        void (async () => {
          try {
            const remote = await pushClientXeroRemote(client.id);
            await putClientCached({
              ...client,
              ...remote,
              pendingOp: null,
              lastUsedAt: client.lastUsedAt,
            });
            void renderClientDetail(client.id);
          } catch (err) {
            window.alert(err instanceof Error ? err.message : 'Xero push failed');
          }
        })();
      });
      root.querySelectorAll('[data-xero-link]').forEach((el) => {
        el.addEventListener('click', () => {
          const contactId = (el as HTMLElement).dataset.xeroLink!;
          void (async () => {
            try {
              const remote = await linkClientXeroRemote(client.id, contactId);
              await putClientCached({
                ...client,
                ...remote,
                pendingOp: null,
                lastUsedAt: client.lastUsedAt,
              });
              void renderClientDetail(client.id);
            } catch (err) {
              window.alert(err instanceof Error ? err.message : 'Xero link failed');
            }
          })();
        });
      });
      if (mix && mixRows.length > 0) {
        bindDonutFilter(
          root,
          donutId,
          () => mixFilter,
          (key) => {
            mixFilter = key;
            paintDetail();
          },
        );
      }
    }

    paintDetail();
  }

  async function renderWorkTypes() {
    root.innerHTML = `
      <section class="more-panel">
        <button type="button" class="btn-back" data-back>‹ More</button>
        <h1 class="screen-title">Work types</h1>
        ${listSkeletonHtml(4, 'list-skel-row skel-ref')}
      </section>
    `;
    root.querySelector('[data-back]')?.addEventListener('click', () => {
      view = 'menu';
      void refresh();
    });

    let listError = false;
    let types: Awaited<ReturnType<typeof listWorkTypesCached>> = [];
    try {
      types = await listWorkTypesCached();
    } catch {
      listError = true;
    }

    const body = listError
      ? listErrorHtml()
      : types.length === 0
        ? listEmptyHtml(
            'No work types cached yet. Unlock online so sync can download them.',
          )
        : `<ul class="ref-list">${types
            .map(
              (t) =>
                `<li><strong>${escape(t.short_description)}</strong> <span class="muted">(${escape(t.line_kind ?? 'day_rate')})</span></li>`,
            )
            .join('')}</ul>`;

    root.innerHTML = `
      <section class="more-panel">
        <button type="button" class="btn-back" data-back>‹ More</button>
        <h1 class="screen-title">Work types</h1>
        ${body}
      </section>
    `;
    root.querySelector('[data-back]')?.addEventListener('click', () => {
      view = 'menu';
      void refresh();
    });
    root.querySelector('[data-list-retry]')?.addEventListener('click', () => {
      void renderWorkTypes();
    });
  }

  async function renderSites() {
    root.innerHTML = `
      <section class="more-panel">
        <button type="button" class="btn-back" data-back>‹ More</button>
        <h1 class="screen-title">Sites</h1>
        ${listSkeletonHtml(5, 'list-skel-row skel-ref')}
      </section>
    `;
    root.querySelector('[data-back]')?.addEventListener('click', () => {
      view = 'menu';
      void refresh();
    });

    let listError = false;
    let sites: Awaited<ReturnType<typeof listSitesCached>> = [];
    try {
      sites = await listSitesCached();
    } catch {
      listError = true;
    }

    const body = listError
      ? listErrorHtml()
      : sites.length === 0
        ? listEmptyHtml(
            'No sites cached yet. Sites appear after seeding or when jobs sync them down.',
          )
        : `<ul class="ref-list">${sites.map((s) => `<li>${escape(s.name)}</li>`).join('')}</ul>`;

    root.innerHTML = `
      <section class="more-panel">
        <button type="button" class="btn-back" data-back>‹ More</button>
        <h1 class="screen-title">Sites</h1>
        ${body}
      </section>
    `;
    root.querySelector('[data-back]')?.addEventListener('click', () => {
      view = 'menu';
      void refresh();
    });
    root.querySelector('[data-list-retry]')?.addEventListener('click', () => {
      void renderSites();
    });
  }

  await refresh();
  return { refresh, showSettings, showRenewals, showExpenses, showCompliance };
}

function escape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
